// Web-side completion-options for the human-completed action kinds (keyed by demodulized
// actionType). This is the ONLY action knowledge left on the FE, and it's temporary: action
// label / description / required / produces now come from the API (OLIO-2574), and the option
// buttons (label / tone) + their output mapping are slated to move to the definition next
// (Phase 2). Kinds with no options (the automated AI pair) aren't listed — the FE renders them
// purely from the API. When options land in the API, this file goes away entirely.
import Workflow from 'models/Workflow';

export interface ActionOption {
  label: string;
  tone?: 'primary' | 'danger';
  // Exactly the `outputs` body to PATCH when this option is chosen. The action's own output
  // only — compound consequences (e.g. a possible duplicate ending the referral) are derived
  // by the kind on the backend, not posted from here (OLIO-2574 Phase A).
  outputs: Record<string, string>;
}

export interface ActionKindMeta {
  // Shown above the options when the action needs a person.
  prompt?: string;
  // The manual completion choices; omitted kinds render status-only.
  options?: ActionOption[];
  // The declared value whose stored content identifies the chosen option, so a
  // completed card can show the recorded result.
  resultValueName?: string;
  // Friendly display text for resultValueName content when the kind has no options
  // to derive a label from (automated kinds). Falls back to the raw value.
  resultLabels?: Record<string, string>;
  // A link rendered with the completed result — e.g. the referral a disposition
  // created or merged into. The target id is read from the named value.
  resultLink?: { valueName: string; label: string; path: (id: string) => string };
  // Value names summarized on the completed card (e.g. what retrieval pulled).
  resultSummaryValues?: string[];
  // For fanned-out (per-building) kinds: short labels for the header rollup counts,
  // keyed by the recorded result value ("2 Accepted • 1 Denied • 1 Pending").
  countLabels?: Record<string, string>;
  // Instructional copy + inline actions when the step is flagged (a person must supply
  // something before it can run) — the Figma flagged card. Kinds without these fall
  // back to naming the missing values. `flaggedPromptSatisfied` replaces the copy when
  // the step's produced values already exist (manual entry): the flag isn't a failure
  // then, and we must not tell the user to do what they already did.
  flaggedPrompt?: string;
  flaggedPromptSatisfied?: string;
  flaggedLinks?: { label: string; section: 'documents' | 'demographics'; hideWhenSatisfied?: boolean }[];
  // The skipped (Not Applicable) row treatment (OLIO-2633, Option C): explanatory copy
  // and, when the step can still be fed later, the affordance that revives it.
  skippedCopy?: string;
  skippedAction?: { label: string; section: 'documents' | 'demographics' };
  // Which states this kind renders a body in, beyond what the fields above already imply
  // (`options` for action_required, `resultValueName`/`resultSummaryValues` for completed).
  // These three used to be bare `action.kind === '...'` comparisons inside CategoryCard: a
  // typo there silently killed the branch, and a kind renamed on the API side broke it with
  // no compile error. Declared here so per-kind knowledge lives beside the rest of it.
  //
  // `bodyWhenActionRequired` is for kinds whose action_required state renders a bespoke form
  // or panel rather than generic option buttons.
  bodyWhenActionRequired?: boolean;
  // For kinds that complete autonomously and still own a completed surface without exposing
  // a recorded result value to render.
  detailWhenCompleted?: boolean;
  // For kinds whose failure state is itself actionable (portal retrieval falls back to the
  // manual form as recovery).
  detailWhenErrored?: boolean;
  // The completed card opens its own review slideout.
  //
  // Mutually exclusive with `options` and `resultValueName` — the slideout IS the kind's surface,
  // so declaring either alongside it renders the generic option prompt on top of the slideout
  // trigger. Making an existing kind slideout-backed therefore means ADDING this and DELETING
  // those, never keeping both. **If you're resolving a merge conflict in this file, that's the
  // trap**: both sides look reasonable and nothing else fails. `actionConfig.test.ts` enforces it.
  reviewSlideout?: boolean;
}

export const ACTION_KINDS: Record<string, ActionKindMeta> = {
  ProcessAttachments: {
    // Completes with a packet summary rather than a recorded result value.
    detailWhenCompleted: true,
    flaggedPrompt: 'Upload referral documentation for Olio to extract patient demographics and clinical details.',
    flaggedLinks: [{ label: 'Add documents', section: 'documents' }],
    skippedCopy: 'Documents weren’t provided — demographics were entered manually.',
    skippedAction: { label: 'Add Documents', section: 'documents' },
  },
  ExtractAttributes: {
    // Completes with the extracted-demographics readout rather than a recorded result value.
    detailWhenCompleted: true,
    flaggedPrompt: 'Demographic information could not be extracted from documentation.',
    flaggedPromptSatisfied:
      'Demographics were added manually. Upload the referral documentation anytime and Olio will process it.',
    flaggedLinks: [
      { label: 'Add documents', section: 'documents' },
      { label: 'Manually add demographics', section: 'demographics', hideWhenSatisfied: true },
    ],
    skippedCopy: 'Nothing to extract — demographics were entered manually.',
  },
  DuplicatePatientSearch: {
    // action_required renders the candidate-review body (DuplicatePatientReview,
    // OLIO-2729) instead of generic option buttons.
    bodyWhenActionRequired: true,
    resultValueName: 'duplicate_patient_result',
    resultLabels: {
      no_match: 'No duplicate referrals found',
      confirmed_match: 'Confirmed duplicate — this referral was canceled',
      // Legacy value from the pre-OLIO-2729 stub contract; kept so old completions render.
      possible_match: 'Possible duplicate found',
    },
  },
  // Both of these complete on their own and are reviewed in a dedicated slideout, so they declare
  // NO options and NO result value. That absence is deliberate and load-bearing — adding either
  // back would re-render a legacy option prompt on top of the slideout trigger. The flags they DO
  // carry say only which states render a body; that used to live as bare kind comparisons inside
  // CategoryCard, where a typo silently disabled a branch.
  //
  // SexOffenderCheck (OLIO-2391) is integration-backed (offenders.io via the Olio integration
  // platform) and its slideout — SexOffenderCheckReview — owns Dismiss/Match mutations against a
  // run-scoped `sex_offender_match_reviews` value.
  SexOffenderCheck: {
    detailWhenCompleted: true,
    reviewSlideout: true,
  },
  // DenialCheck's slideout (DenialCheckReview) is READ-ONLY by contrast — it lists the matched
  // entries the check found; there is no confirm/dismiss and no mutation, because the determination
  // is derived server-side and surfaced as a flag via the API's effectiveStatus.
  DenialCheck: {
    detailWhenCompleted: true,
    reviewSlideout: true,
  },
  BenefitsEligibility: {
    prompt: 'Record the insurance verification result.',
    resultValueName: 'benefits_eligibility_result',
    options: [
      { label: 'Coverage active', outputs: { benefits_eligibility_result: 'active' } },
      { label: 'Coverage inactive', tone: 'danger', outputs: { benefits_eligibility_result: 'inactive' } },
    ],
  },
  // FacilityDecision's Accept/Deny interaction (buttons, note modal, bulk links) lives
  // entirely in FacilityDecisionPanel — only the rollup/result display metadata is here.
  FacilityDecision: {
    resultValueName: 'facility_determination',
    countLabels: { accepted: 'Accepted', denied: 'Denied' },
  },
  // AcceptReferral ("Referral Outcome") completes through its own always-available panel
  // (ReferralOutcomePanel) and renders its completed state as per-building verdict rows
  // (ReferralOutcomeResult) — only the result declaration lives here.
  AcceptReferral: {
    bodyWhenActionRequired: true,
    resultValueName: 'final_outcome',
  },

  // Portal Automation — labels/descriptions/contracts come from the API like
  // every other kind; only FE display concerns live here. Retrieval renders a custom form
  // (RetrievePortalForm), not options; disposition is automated.
  RetrievePortalInformation: {
    bodyWhenActionRequired: true,
    // A failed retrieval still renders — the manual form is the recovery path.
    detailWhenErrored: true,
    resultSummaryValues: ['attachments', 'facilities'],
  },
  // Facility mapping resolves the collected portal strings automatically and only
  // surfaces when a string has no saved mapping — its action_required body
  // (FacilityMappingReview) owns that interaction; a completed pass needs no detail.
  MapPortalFacilities: {
    bodyWhenActionRequired: true,
  },
  // Portal Discovery — a sweep hands off to the extension (PortalDiscoveryBody) and
  // completes by submitting what it found, so it declares no completion options. Its
  // results are NOT a resultSummaryValues entry: the generic summary reads workflow-level
  // values and hides empty lists, but a sweep must read THIS run's own output (design §6c)
  // and must say "found nothing" out loud rather than rendering blank. DiscoveredItems
  // (PortalDiscoveryBody) owns that instead.
  // A sweep is driven entirely from its own body (the extension hand-off) and declares no
  // options, so without `bodyWhenActionRequired` it renders as a bare "Ready for Review" row
  // with nothing to click. And a completed sweep always has something to say — the items it
  // found, or that it found nothing, which is a result rather than an empty state — so it needs
  // `detailWhenCompleted` even though it records no result value the generic renderer could use.
  // (OLIO-2757 declared both as kind comparisons inside CategoryCard; same behavior, declared
  // here instead.)
  DiscoverPortalActivity: {
    bodyWhenActionRequired: true,
    detailWhenCompleted: true,
  },
  DisposePortalReferral: {
    resultValueName: 'portal_automation_outcome',
    resultLabels: {
      created: 'Created a new referral',
      merged: 'Merged into an existing referral',
    },
    resultLink: {
      valueName: 'disposed_workflow_id',
      label: 'View referral',
      path: (id: string) => `/inbound-referrals/${id}`,
    },
  },
};

export function actionKindMeta(kind: string): ActionKindMeta | undefined {
  return ACTION_KINDS[kind];
}

// A stopgap humanization of a declared (snake_case) value name (`patient_dob` ->
// `Patient dob`), used only when the API hasn't supplied a catalog label.
export function valueLabel(name: string): string {
  const spaced = name.replace(/_/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

// The display label for a value: the API's catalog label (design P1-5) when present,
// else the humanized name.
export function labelForValue(workflow: Workflow, name: string): string {
  return workflow.valueFor(name)?.label ?? valueLabel(name);
}
